import React, { useEffect, useMemo, useState } from 'react';

import {
  Button,
  SearchableDropdown,
  SideBarModal,
  showErrorToast,
  showSuccessToast,
} from '@/components';
import {
  ExportTemplateSummary,
  exportShipmentsCSV,
  listExportTemplates,
  ShipmentListParams,
} from '@/services/api/shipments';

import { getShipmentEximLabel } from './ShipmentEximBadge';

type ExportMode = 'default' | 'custom';

// Mirrors the ops export modal: Default = current filters with the client's
// configured layout; Custom = pick a specific template. Link has no client
// picker — the signed-in client is the scope — and no direction picker: the
// list's own direction filter already flows in through the base params.
const MODES: { mode: ExportMode; label: string; desc: string }[] = [
  {
    mode: 'default',
    label: 'Default export',
    desc: 'Filter daftar saat ini, layout yang dikonfigurasi untuk akun Anda.',
  },
  {
    mode: 'custom',
    label: 'Custom export',
    desc: 'Filter daftar saat ini, dengan template pilihan Anda.',
  },
];

interface ExportShipmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The list's current filters (minus pagination) — the export mirrors them. */
  baseParams: Omit<ShipmentListParams, 'page' | 'limit'>;
}

/**
 * "Export CSV" for Dash Link.
 *
 * Default export mirrors the on-screen filters and lets the backend pick the
 * client's configured layout. Custom export lets the client pick one of the
 * templates it may use — the gateway scopes that list to the client, so
 * another client's layout never appears here.
 */
const ExportShipmentsModal: React.FC<ExportShipmentsModalProps> = ({
  isOpen,
  onClose,
  baseParams,
}) => {
  const [mode, setMode] = useState<ExportMode>('default');
  const [templateKey, setTemplateKey] = useState('');
  const [templates, setTemplates] = useState<ExportTemplateSummary[] | null>(
    null
  );
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Fresh state each open: back to Default, no template picked.
  useEffect(() => {
    if (!isOpen) return;
    setMode('default');
    setTemplateKey('');
  }, [isOpen]);

  // Templates are only needed for Custom — fetch once, the first time it's
  // picked while the modal is open.
  useEffect(() => {
    if (!isOpen || mode !== 'custom' || templates !== null) return;
    let cancelled = false;
    setTemplatesError(null);
    listExportTemplates()
      .then((t) => {
        if (!cancelled) setTemplates(t);
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplates([]);
        setTemplatesError(
          err instanceof Error ? err.message : 'Gagal memuat template.'
        );
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, templates]);

  // Show each template's direction next to its key so a client with both
  // Patimban layouts can tell export from import at a glance.
  const templateOptions = useMemo(
    () =>
      (templates ?? []).map((t) => ({
        value: t.key,
        label: t.exim ? `${t.key} · ${getShipmentEximLabel(t.exim)}` : t.key,
      })),
    [templates]
  );

  const handleExport = async () => {
    if (mode === 'custom' && !templateKey) {
      showErrorToast('Pilih template', 'Custom export membutuhkan template.');
      return;
    }
    setExporting(true);
    try {
      await exportShipmentsCSV(
        mode === 'custom' ? { ...baseParams, templateKey } : baseParams
      );
      showSuccessToast('Export dimulai', 'File CSV sedang diunduh.');
      onClose();
    } catch (err) {
      showErrorToast(
        'Export gagal',
        err instanceof Error ? err.message : 'Terjadi kesalahan.'
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={exporting ? () => undefined : onClose}
      width="sm"
      position="center"
      closeOnBackdropClick={!exporting}
      header={
        <div>
          <h2 className="text-lg font-semibold text-text-strong-950">
            Export shipments
          </h2>
          <p className="mt-0.5 text-xs text-text-sub-600">
            Pilih cara export.
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={exporting}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleExport} disabled={exporting}>
            {exporting ? (
              <>
                <i className="ri-loader-4-line mr-1.5 animate-spin" />
                Exporting…
              </>
            ) : (
              <>
                <i className="ri-download-2-line mr-1.5" />
                Export CSV
              </>
            )}
          </Button>
        </div>
      }
    >
      {/* SideBarModal pads its header/footer but not the body — pad here. */}
      <div className="flex flex-col gap-3 px-6 py-5">
        {MODES.map((m) => {
          const active = mode === m.mode;
          return (
            <button
              key={m.mode}
              type="button"
              onClick={() => setMode(m.mode)}
              disabled={exporting}
              className={`flex flex-col items-start gap-0.5 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                active
                  ? 'border-primary bg-primary/5'
                  : 'border-stroke-soft-200 hover:border-stroke-sub-300'
              }`}
            >
              <span className="text-sm font-semibold text-text-strong-950">
                {m.label}
              </span>
              <span className="text-xs text-text-sub-600">{m.desc}</span>
            </button>
          );
        })}

        {mode === 'custom' && (
          <div className="mt-1 border-t border-stroke-soft-200 pt-3">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-soft-400">
              Template
            </h4>
            <SearchableDropdown
              options={templateOptions}
              value={templateKey || undefined}
              onChange={(v) => setTemplateKey(v)}
              placeholder={
                templates === null ? 'Memuat template…' : 'Pilih template…'
              }
              loading={templates === null}
              disabled={exporting}
              allowClear
              emptyMessage={templatesError ?? 'Tidak ada template'}
            />
            {templatesError ? (
              <p className="mt-1.5 text-[11px] text-error-base">
                {templatesError}
              </p>
            ) : (
              <p className="mt-1.5 text-[11px] text-text-soft-400">
                Hanya template untuk akun Anda yang tampil.
              </p>
            )}
          </div>
        )}
      </div>
    </SideBarModal>
  );
};

export default ExportShipmentsModal;
